/**
 * 모바일 「문제 설정」 바텀시트 — 조건 칩(⚙︎)과 그 칩이 여는 시트. 순수 UI 다. Figma 6-3 · 6-4.
 * 손잡이 → 헤더(제목·✕) → 문제 수 세그먼트 · 유형(여러 개) · 난이도 → 「완료」. 시트 안에서 고른 값은
 * 「완료」를 눌러야 적용된다 — 그냥 닫으면 버린다. 유형·난이도는 시트 안에서 드롭다운으로 연다(중첩 시트 없음).
 */
import { useState } from 'react';
import { IconButton, Tabs, TabsList, TabsTrigger } from '@zep-us/zus';
import { Button } from '@zep-us/zus/button';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { XIcon } from '@zep-us/zus-icons';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle, DrawerTrigger } from '@/ui_v3';
import { MultiSelect, SingleSelect } from '@/ui_v3/components/select';
import { AI_GENERATE_DOCK_DIFFICULTIES, AI_GENERATE_DOCK_QUIZ_TYPES, type AiGenerateDockDifficulty } from '../consts/aiGenerateDock.const';
import { type AiGenerateDockConditions, type AiGenerateDockCopy } from '../types/aiGenerateDock.types';
import { type AiGenerateDockCountOption } from '../utils/aiGenerateDockCount';
import { type AiBuilderQuizTypeHint } from '../utils/types';
import { AiGenerateDockConditionsTrigger } from './AiGenerateDockConditionsTrigger';
export type AiGenerateDockConditionsSheetProps = {
  copy: AiGenerateDockCopy;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 초안의 시작값 — 문제 수도 남은 칸으로 줄이기 전 고른 값 그대로(손대지 않고 저장하면 보존된다). */
  conditions: AiGenerateDockConditions;
  /** 남은 칸보다 큰 프리셋은 꺼진다. */
  countOptions: AiGenerateDockCountOption[];
  onSave: (conditions: AiGenerateDockConditions) => void;
  /** 칩에 보이는 요약 — 좁은 화면에선 아이콘만 보이고 aria-label 로 읽힌다. */
  summary: string;
  disabled?: boolean;
};
const toDifficulty = (value: string): AiGenerateDockDifficulty => AI_GENERATE_DOCK_DIFFICULTIES.find(item => String(item) === value) ?? AI_GENERATE_DOCK_DIFFICULTIES[0];
export const AiGenerateDockConditionsSheet = ({
  copy,
  open,
  onOpenChange,
  conditions,
  countOptions,
  onSave,
  summary,
  disabled
}: AiGenerateDockConditionsSheetProps) => {
  const [draft, setDraft] = useState(conditions);
  // 여는 순간 지금 조건에서 시작한다 — 닫으면 고르던 값은 버린다.
  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(conditions);
    onOpenChange(next);
  };
  return <Drawer open={open} onOpenChange={handleOpenChange} data-sentry-element="Drawer" data-sentry-component="AiGenerateDockConditionsSheet" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
      <DrawerTrigger asChild data-sentry-element="DrawerTrigger" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
        <AiGenerateDockConditionsTrigger iconOnly label={copy.conditionsToggle} summary={summary} disabled={disabled} data-sentry-element="AiGenerateDockConditionsTrigger" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx" />
      </DrawerTrigger>
      <DrawerContent hasDrawerHandle hasDim data-testid="ai-generate-dock-conditions-sheet" className="inset-x-0 flex flex-col rounded-t-md" data-sentry-element="DrawerContent" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
        <div className="flex items-center px-md pb-xs pt-md">
          <DrawerTitle className="flex-1 text-heading-md-bold text-default" data-sentry-element="DrawerTitle" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
            {copy.conditionsSheetTitle}
          </DrawerTitle>
          <DrawerClose asChild data-sentry-element="DrawerClose" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
            <IconButton size="sm" variant="transparent" intent="neutral" aria-label={copy.close} data-sentry-element="IconButton" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
              <XIcon size="sm" fill={ICON_COLOR.default} data-sentry-element="XIcon" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx" />
            </IconButton>
          </DrawerClose>
        </div>
        <div className="flex flex-col gap-md px-md pb-md pt-xs">
          <div className="flex flex-col gap-3xs">
            <p className="text-body-lg-bold text-default">{copy.countLabel}</p>
            <Tabs
          // 고른 값을 그대로 켠다 — 남은 칸보다 커도 고를 수 있다(넓은 화면 메뉴와 같다).
          value={String(draft.count)} onValueChange={value => setDraft({
            ...draft,
            count: Number(value)
          })} data-sentry-element="Tabs" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
              <TabsList className="flex h-auto w-full rounded-sm bg-alternative p-3xs" data-sentry-element="TabsList" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
                {countOptions.map(option => <TabsTrigger key={option.value} value={String(option.value)} data-testid={`ai-generate-dock-sheet-count-${option.value}`} className="flex-1 rounded-sm py-xs text-body-lg-semibold text-assistive data-[state=active]:bg-default data-[state=active]:text-default data-[state=active]:shadow-sm">
                    {option.value}
                  </TabsTrigger>)}
              </TabsList>
            </Tabs>
          </div>
          <MultiSelect<AiBuilderQuizTypeHint> title={copy.quizTypeLabel} mobilePresentation="dropdown" values={draft.quizTypes} options={AI_GENERATE_DOCK_QUIZ_TYPES.map(type => ({
          value: type,
          label: copy.quizTypes[type]
        }))} onValuesChange={values => {
          // 유형은 1개 이상 — 마지막 하나는 뺄 수 없다.
          if (values.length === 0) return;
          setDraft({
            ...draft,
            quizTypes: AI_GENERATE_DOCK_QUIZ_TYPES.filter(type => values.includes(type))
          });
        }} data-sentry-element="MultiSelect" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx" />
          <SingleSelect title={copy.difficultyLabel} mobilePresentation="dropdown" value={String(draft.difficulty)} options={AI_GENERATE_DOCK_DIFFICULTIES.map(difficulty => ({
          value: String(difficulty),
          label: copy.difficulties[difficulty]
        }))} onValueChange={value => {
          if (value) setDraft({
            ...draft,
            difficulty: toDifficulty(value)
          });
        }} data-sentry-element="SingleSelect" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx" />
        </div>
        <div className="px-md pb-lg pt-xs">
          <Button size="lg" variant="contained" intent="strong" className="w-full" data-testid="ai-generate-dock-sheet-save" onClick={() => {
          onSave(draft);
          onOpenChange(false);
        }} data-sentry-element="Button" data-sentry-source-file="AiGenerateDockConditionsSheet.tsx">
            {copy.conditionsSheetSave}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>;
};